<template>
    <v-form @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header :title="title" />
            <v-card-text>
                <v-row>
                    <v-col>
                        <v-text-field
                            autofocus
                            v-model="lp.name"
                            :label="$t('resources.landingPage.attributes.name')"
                            @input="$v.lp.name.$touch()"
                            @blur="$v.lp.name.$touch()"
                            :error-messages="nameErrors"
                        />
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="lp.url"
                            :label="$t('resources.landingPage.attributes.url')"
                            @input="$v.lp.url.$touch()"
                            @blur="$v.lp.url.$touch()"
                            :error-messages="urlErrors"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-autocomplete
                            v-model="selectedResellers"
                            :items="resellers"
                            item-text="name"
                            item-value="id"
                            chips
                            deletable-chips
                            multiple
                            return-object
                            :loading="loadingResellers"
                            :search-input.sync="resellerSearchInput"
                            :placeholder="$t('forms.placeHolders.typeToSearch')"
                            :label="
                                $t('resources.landingPage.attributes.resellers')
                            "
                            @input="resellerSearchInput = null"
                        ></v-autocomplete>
                    </v-col>
                </v-row>
            </v-card-text>
            <FormActions :loadingSave="saving" />
        </v-card>
        <v-card-text> </v-card-text>
    </v-form>
</template>

<script>
import FormActions from '@/components/utils/FormActions'
import LandingPage from '@/models/LandingPage'
import Reseller from '@/models/Reseller'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

export default {
    components: {
        FormActions,
    },

    mixins: [validationMixin],

    props: {
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof LandingPage
            },
        },
        titlePrefix: String,
    },

    data: () => {
        return {
            loadingResellers: false,
            resellerSearchInput: null,
            resellers: [],
            selectedResellers: [],
            saving: false,
        }
    },

    validations() {
        return {
            lp: {
                name: { required },
                url: { required },
            },
        }
    },

    watch: {
        resellerSearchInput(val) {
            if (val === null) return
            if (this.loadingResellers) return

            this.loadingResellers = true

            this.searchResellers(val).finally(() => {
                this.loadingResellers = false
            })
        },
    },

    computed: {
        title() {
            return this.$t('forms.title', {
                prefix: this.titlePrefix,
                resource: this.$tc('resources.landingPage.name', 1),
            })
        },

        lp() {
            return this.model
        },

        nameErrors() {
            const errors = []
            if (!this.$v.lp.name.$dirty) return errors

            !this.$v.lp.name.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t('resources.landingPage.attributes.name'),
                    }),
                )

            return errors
        },

        urlErrors() {
            const errors = []
            if (!this.$v.lp.url.$dirty) return errors

            !this.$v.lp.url.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t('resources.landingPage.attributes.url'),
                    }),
                )

            return errors
        },
    },

    methods: {
        formatReseller(reseller) {
            return {
                name: `[${reseller.tenant_code}] - ${reseller.trade_name}`,
                id: reseller.id,
            }
        },

        async searchResellers(val) {
            let res = await new Reseller()
                .where('search', val)
                .where('active', true)
                .get()

            let resellers = res.data.map(reseller => {
                return this.formatReseller(reseller)
            })

            if (this.selectedResellers.length > 0) {
                this.selectedResellers.forEach(selected => {
                    let hasSelected = resellers.find(reseller => {
                        selected.id === reseller.id
                    })

                    if (hasSelected === undefined) {
                        resellers.push(selected)
                    }
                })
            }

            this.resellers = resellers
        },

        async save() {
            this.saving = true
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.messages.validation-error'),
                )

                this.saving = false
                return
            }

            this.lp.resellers = []
            this.selectedResellers.forEach(entry =>
                this.lp.resellers.push(entry.id),
            )

            let res
            try {
                res = await this.lp.save().catch(() => {})
            } finally {
                this.saving = false
            }

            if (!res) return
            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('forms.messages.saved', {
                    resource: this.$tc(
                        'resources.landingPage.name',
                        1,
                    ).toLowerCase(),
                    name: this.lp.name,
                }),
            )
        },
    },

    created() {
        if (this.lp.resellers && this.lp.resellers.length > 0) {
            this.lp.resellers.forEach(reseller => {
                let formatted = this.formatReseller(reseller)
                this.resellers.push(formatted)
                this.selectedResellers.push(formatted)
            })
        }
    },
}
</script>

<style></style>
